import { useCallback, useEffect, useState } from "react";
import { api, HttpError, LEVEL_ADMIN, LEVEL_CAMERA_VIEWER, LEVEL_TEAM_MEMBER } from "../api/client";
import type { AdminUser } from "../api/types";

/**
 * Admin view of who exists. This screen is the replacement for editing
 * `BUACAM_USERS` on the host — that has been a silent no-op since the `users`
 * table became the sole authority (ADR-0001), so without this there is no
 * supported way to add a person at all.
 *
 * <p>Email is rendered as text, never an input (ADR-0002). The social tables
 * and both audit trails store it as a denormalised string with no foreign key,
 * so renaming someone here would detach their whole history — the field is
 * absent from the API too, not merely disabled in the UI.
 *
 * <p>Nobody is ever deleted. Deactivating keeps their comments and their past
 * Responses answering truthfully, which is the whole reason the row survives.
 */

const LEVELS = [
  { code: LEVEL_CAMERA_VIEWER, label: "Camera viewer", hint: "Browse, like, comment" },
  { code: LEVEL_TEAM_MEMBER, label: "Team member", hint: "+ Schedule, tagging, hiding" },
  { code: LEVEL_ADMIN, label: "Admin", hint: "+ Audit trail, user management" },
] as const;

export function UsersScreen() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await api.listUsers());
    } catch (e) {
      setError(
        e instanceof HttpError && e.status === 403
          ? "Your account is not permitted to manage users."
          : "Could not load the user list. Try again in a moment.",
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const replace = useCallback((u: AdminUser) => {
    setUsers((prev) => prev.map((p) => (p.userId === u.userId ? u : p)));
  }, []);

  return (
    <div className="audit">
      <div className="audit-hd">
        <div>
          <div className="eyebrow">Administration</div>
          <h1 className="audit-title">People</h1>
        </div>
        <button className="chip active" type="button" onClick={() => setAdding(true)}>
          Add person
        </button>
      </div>

      {error && <p className="audit-empty">{error}</p>}
      {notice && <p className="audit-note">{notice}</p>}
      {loading && !error && <p className="audit-empty">Loading…</p>}

      {!loading && !error && (
        <div className="audit-table-wrap">
          <table className="audit-table users-table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Name</th>
                <th scope="col">Access level</th>
                <th scope="col">Sign-in</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow
                  key={u.userId}
                  user={u}
                  onChanged={replace}
                  onNotice={setNotice}
                  onError={setError}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddUserDialog
          onClose={() => setAdding(false)}
          onCreated={(u) => {
            setUsers((prev) => [...prev, u].sort((a, b) => a.email.localeCompare(b.email)));
            setAdding(false);
            setNotice(`${u.email} can now sign in.`);
          }}
        />
      )}
    </div>
  );
}

function UserRow({
  user,
  onChanged,
  onNotice,
  onError,
}: {
  user: AdminUser;
  onChanged: (u: AdminUser) => void;
  onNotice: (s: string | null) => void;
  onError: (s: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(user.displayName ?? "");

  /**
   * Every mutation funnels through here so the last-admin refusal is reported
   * the same way wherever it comes from. It is a 409, not a failure: the server
   * is telling us the change would leave nobody able to manage users.
   */
  async function run(what: () => Promise<AdminUser | void>, success?: string) {
    if (busy) return;
    setBusy(true);
    onError(null);
    onNotice(null);
    try {
      const updated = await what();
      if (updated) onChanged(updated);
      if (success) onNotice(success);
    } catch (e) {
      onError(
        e instanceof HttpError && e.status === 409
          ? `${user.email} is the last active admin — promote someone else first.`
          : `Could not update ${user.email}.`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <tr>
        {/* Text, not an input. Email is immutable after creation (ADR-0002). */}
        <td className="audit-who">{user.email}</td>

        <td>
          {editingName ? (
            <input
              className="users-inline-input"
              type="text"
              maxLength={128}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => {
                setEditingName(false);
                if ((user.displayName ?? "") !== name) {
                  void run(() => api.updateUser(user.userId, { displayName: name }));
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                if (e.key === "Escape") {
                  setName(user.displayName ?? "");
                  setEditingName(false);
                }
              }}
            />
          ) : (
            <button type="button" className="sched-link" onClick={() => setEditingName(true)}>
              {user.displayName || <span className="users-unset">Set a name</span>}
            </button>
          )}
        </td>

        <td>
          <select
            className="users-level"
            value={user.accessLevel}
            disabled={busy}
            onChange={(e) =>
              void run(() => api.updateUser(user.userId, { accessLevel: Number(e.target.value) }))
            }
          >
            {LEVELS.map((l) => (
              <option key={l.code} value={l.code} title={l.hint}>
                {l.label}
              </option>
            ))}
          </select>
        </td>

        <td className="users-signin">{user.hasPassword ? "Password + SSO" : "SSO only"}</td>

        <td>
          <span className={`audit-badge${user.active ? "" : " fail"}`}>
            <span className="dot" aria-hidden="true" />
            {user.active ? "Active" : "Deactivated"}
          </span>
        </td>

        <td className="users-actions">
          <button
            type="button"
            className="sched-link"
            disabled={busy}
            onClick={() => setResetting((r) => !r)}
          >
            {user.hasPassword ? "Reset password" : "Set password"}
          </button>
          <button
            type="button"
            className="sched-link"
            disabled={busy}
            onClick={() =>
              void run(
                () => api.updateUser(user.userId, { active: !user.active }),
                user.active
                  ? `${user.email} can no longer sign in. Their comments and answers are untouched.`
                  : `${user.email} can sign in again.`,
              )
            }
          >
            {user.active ? "Deactivate" : "Reactivate"}
          </button>
        </td>
      </tr>

      {resetting && (
        <tr className="audit-detail-row">
          <td colSpan={6}>
            <div className="users-reset">
              <label className="ev-field">
                <span>New password for {user.email}</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                />
              </label>
              <div className="ev-actions">
                <button
                  type="button"
                  className="chip"
                  onClick={() => {
                    setResetting(false);
                    setNewPassword("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="chip active"
                  disabled={busy || newPassword.length < 8}
                  onClick={() =>
                    void run(async () => {
                      await api.resetUserPassword(user.userId, newPassword);
                      setResetting(false);
                      setNewPassword("");
                      // hasPassword flips for an SSO-only user, so re-read
                      // rather than guessing at the new row.
                      const all = await api.listUsers();
                      return all.find((u) => u.userId === user.userId);
                    }, `Password set for ${user.email}. Tell them in person, not by email.`)
                  }
                >
                  Set password
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function AddUserDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (u: AdminUser) => void;
}) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [accessLevel, setAccessLevel] = useState<number>(LEVEL_TEAM_MEMBER);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      onCreated(
        await api.createUser({
          email: email.trim(),
          password: password || undefined,
          accessLevel,
          displayName: displayName.trim() || undefined,
        }),
      );
    } catch (err) {
      setError(
        err instanceof HttpError && err.status === 409
          ? "Someone already has that email address."
          : "Could not create the account. Check the email and password and try again.",
      );
      setBusy(false);
    }
  }

  return (
    <div className="ev-overlay" role="presentation" onClick={onClose}>
      <div
        className="ev-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Add person"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={submit}>
          <div className="ev-dialog-hd">
            <h2 className="serif">Add person</h2>
            <button type="button" className="ev-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="ev-grid">
            <label className="ev-field ev-wide">
              <span>Email</span>
              <input
                type="email"
                required
                autoFocus
                maxLength={128}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="erik@example.com"
              />
              {/* Said once, at the only moment it can still be changed. */}
              <small className="ev-hint">
                This is their username, and it cannot be changed later.
              </small>
            </label>

            <label className="ev-field">
              <span>Name</span>
              <input
                type="text"
                maxLength={128}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="optional"
              />
            </label>

            <label className="ev-field">
              <span>Access level</span>
              <select value={accessLevel} onChange={(e) => setAccessLevel(Number(e.target.value))}>
                {LEVELS.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label} — {l.hint}
                  </option>
                ))}
              </select>
            </label>

            <label className="ev-field ev-wide">
              <span>Initial password</span>
              <input
                type="password"
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
              />
              <small className="ev-hint">
                Leave blank if they will only ever sign in with Google or Microsoft. They can change
                it themselves afterwards.
              </small>
            </label>
          </div>

          {error && <p className="ev-error">{error}</p>}

          <div className="ev-actions">
            <button type="button" className="chip" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="chip active" disabled={busy}>
              {busy ? "Creating…" : "Create account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
